import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

import { tr } from "../lib/i18n";
/** Generic two-field list manager used for chat FAQs and canned replies. */
function ListManager({ endpoint, fields, titleKey, bodyKey, heading, testid }) {
  const { can } = useAuth();
  const canEdit = can("chat", "edit");
  const canDelete = can("chat", "delete");
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({});
  const reload = async () => {
    try { setItems((await api.get(endpoint)).data || []); } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [endpoint]);
  const add = async () => {
    if (!fields.every((f) => (form[f.key] || "").trim())) return;
    try { await api.post(endpoint, form); setForm({}); reload(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm(tr("x.delete_this_item"))) return;
    try { await api.delete(`${endpoint}/${id}`); reload(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="grid gap-[2px]" data-testid={testid}
      style={{ background: "var(--divider)", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%,320px), minmax(0,1fr)))" }}>
      {canEdit && <div style={{ background: "#fff", padding: 20, display: "grid", gap: 10, alignContent: "start" }}>
        <div className="kicker">{tr("x.add")} {heading}</div>
        {fields.map((f) => f.multiline ? (
          <textarea key={f.key} rows={4} placeholder={f.label} value={form[f.key] || ""} className="input" style={{ height: "auto", padding: 10 }}
            onChange={(e) => setForm((x) => ({ ...x, [f.key]: e.target.value }))} data-testid={`${testid}-${f.key}`} />
        ) : (
          <input key={f.key} placeholder={f.label} value={form[f.key] || ""} className="input"
            onChange={(e) => setForm((x) => ({ ...x, [f.key]: e.target.value }))} data-testid={`${testid}-${f.key}`} />
        ))}
        <button className="btn-primary" style={{ width: "fit-content" }} onClick={add} data-testid={`${testid}-add`}><Plus size={14} /> {tr("x.add")}</button>
      </div>}
      <div style={{ background: "#fff", padding: 20 }}>
        <div className="kicker mb-2">{heading} ({items.length})</div>
        {items.length === 0 && <div className="text-sm opacity-60">{tr("x.nothing_yet")}</div>}
        {items.map((it) => (
          <div key={it.id} className="flex items-start justify-between gap-3 py-3" style={{ borderBottom: "1px solid var(--divider-soft)" }}>
            <div className="min-w-0">
              <div className="text-sm font-medium">{it[titleKey]}{String(it[bodyKey] || "").trim().toLowerCase().startsWith("[placeholder]") && <span className="pill ms-2" style={{ background: "#fef3c7", color: "#92400e", fontSize: 10 }} data-testid={`${testid}-placeholder-${it.id}`}>{tr("x.placeholder_edit_before_go_live")}</span>}</div>
              <div className="text-xs opacity-70">{it[bodyKey]}</div>
            </div>
            {canDelete && <button className="btn-ghost" onClick={() => del(it.id)} aria-label={tr("x.delete")} data-testid={`${testid}-delete-${it.id}`}><Trash2 size={14} /></button>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function FaqManager() {
  return <ListManager endpoint="/chat/faqs" heading={tr("x.faq")} testid="faq-manager" titleKey="question" bodyKey="answer"
    fields={[{ key: "question", label: tr("x.question") }, { key: "answer", label: tr("x.answer"), multiline: true }]} />;
}

export function CannedManager() {
  return <ListManager endpoint="/chat/canned" heading="Canned replies" testid="canned-manager" titleKey="title" bodyKey="text"
    fields={[{ key: "title", label: tr("x.title") }, { key: "text", label: tr("x.reply_text"), multiline: true }]} />;
}
